import React, { useState, useMemo, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import Icon from '../../../generic-components/Icon';
import CloseIcon from '../../../assets/svg/x-icon-v2.svg';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { useStore } from '../../../store';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { STEPS, STYLES, AMOUNT_ERROR_MAP } from '../consts';
import { HeaderNotification } from './HeaderNotification';
import { VIEWS_MAP } from '../../../router/constants';
import { ReactComponent as LeftArrow } from '../../../assets/svg/left-arrow-title.svg';
import { trackMixPanel } from '../../../services/mixpanel.service';

export const Amount = () => {
  const {
    setActiveStep, selectedLimit: limit, payeeDetails, setCheckAmount, checkAmount, resetMailCheckSlice, upperMessageContent, setUpperMessageContent,
  } = useStore(state => state);
  const [formError, setFormError] = useState<boolean>(false);
  const [amountValue, setAmountValue] = useState<number>(checkAmount || 0);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateState = {
    referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK,
  };

  const dynamicTitle = useMemo(() => AMOUNT_ERROR_MAP[limit.key as keyof typeof AMOUNT_ERROR_MAP], [limit.key]);

  const handleInput = (value:string) => {
    const intValue = Number(value);
    setAmountValue(intValue < 0 ? 0 : intValue);
    if (intValue > limit.value) {
      setFormError(true);
    } else if (intValue <= limit.value && formError) {
      setFormError(false);
    }
  };

  const handleSubmit = () => {
    setCheckAmount(Number(amountValue));
    trackMixPanel('Act on Move Money Amount Screen', { Flow: 'Mail a Check', Action: 'Continue', Amount: checkAmount });
    setActiveStep(STEPS.REVIEW);
  };

  const handleCloseAmount = () => {
    resetMailCheckSlice();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleBack = () => {
    trackMixPanel('Act on Move Money Amount Screen', { Flow: 'Mail a Check', Action: 'Back' });
    if (checkAmount) {
      setActiveStep(STEPS.REVIEW);
    } else {
      navigate(
        `/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`,
        {
          state: navigateState,
        },
      );
    }
  };

  useEffect(() => {
    trackMixPanel('View Move Money Amount Screen', { Flow: 'Mail a Check' });
    if (!payeeDetails || !payeeDetails.supplierId) {
      setActiveStep(STEPS.INTRO);
    }
    return () => {
      setUpperMessageContent('');
    };
  }, []);

  return (
    <>
      <div className={STYLES.AMOUNT.HEADER_WRAPPER}>
        <Button className='rounded-[50%] hover:bg-bw02 py-[4px] px-[4px] flex items-center justify-center' id='' childElement={<Icon onClick={handleCloseAmount} className={STYLES.AMOUNT.HEADER_ICON} src={CloseIcon} />} />
        <Heading title={t('MAIL_CHECK.AMOUNT.HEADER_TITLE')} className={STYLES.AMOUNT.HEADER_TITLE} />
        <span className='w-[24px]' />
        {upperMessageContent && <HeaderNotification autoCloseSec={8} onTimeElapsed={() => setUpperMessageContent('')} title={upperMessageContent} />}
      </div>
      <div className={STYLES.AMOUNT.BODY_WRAPPER}>
        <div className='relative'>
          <Heading title={t('MAIL_CHECK.AMOUNT.MAIN_HEADER')} className={STYLES.AMOUNT.BODY_MAIN_HEADER} />
          <Button
            onClick={handleBack}
            className='absolute left-0 top-0 translate-x-[-220px] translate-y-[3px] py-[6px] pl-[6px] pr-[10px] rounded-[8px] hover:bg-bw02'
            id='elBackButton'
            childElement={(
              <div className='flex items-center gap-[6px]'>
                <LeftArrow className='w-[16px] stroke-bw08' />
                <Heading title='Back' className='border-b border-bw08' />
              </div>
          )}
          />
        </div>

        <CurrencyInput
          id='inputAmountToTransfer'
          name='amount'
          className={STYLES.AMOUNT.CURRENCY_INPUT}
          placeholder='$0.00'
          decimalsLimit={2}
          autoFocus
          prefix='$'
          maxLength={9}
          defaultValue={checkAmount || ''}
          onValueChange={(value) => handleInput(value || '0')}
        />
        {dynamicTitle && <Heading id='amountError' title={`${dynamicTitle?.replace('|AMOUNT|', convertValueToUSD(limit.value, 2))}`} className={`${formError ? 'opacity-100' : 'opacity-0'} ${STYLES.AMOUNT.INPUT_ERROR}`} />}
      </div>
      <div className={STYLES.AMOUNT.FOOTER_WRAPPER}>
        <Button
          title={t('MAIL_CHECK.AMOUNT.CTA')}
          disable={formError || !amountValue}
          onClick={handleSubmit}
          className={`${STYLES.AMOUNT.CTA} ${(formError || !amountValue) ? 'opacity-50' : ''}`}
          id='submitAmount'
        />
      </div>
    </>
  );
};
